/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, FormEvent, useState, useEffect, useContext } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import { Select, Button } from '@chakra-ui/react';
import Card from "components/card";
import InputField from "components/fields/InputField";
import axios from 'axios';
import DisplayAmountWithDecimals from "./DisplayAmountWithDecimals";
import { useRouter } from "routes/hooks/useRouter";

interface Props {
  isOpen: boolean;
  onClose(): void;
  setSwalProps?: any;
  assets: any;
  fiatAssets: any;
  cryptoAssets: any;
  isFetchingTradeAsset: boolean;
  subaccountBalance: any;
  setSubaccountBalance: any;
  isGettingBalance: any;
  setIsGettingBalance: any;
}

interface TradeData {
  fromAsset: string;
  toAsset: string;
  toAddress: string;
  amount: string;
  reference: string;
}

interface WhitelistAddress {
  id: string;
  address: string;
  label?: string;
}

interface PayloadType {
  [key: string]: any;
}

const OpenTradeModal: React.FC<Props> = ({
  isOpen,
  onClose,
  setSwalProps,
  assets,
  fiatAssets,
  cryptoAssets,
  isFetchingTradeAsset,
  subaccountBalance,
  setSubaccountBalance,
  isGettingBalance,
  setIsGettingBalance
}) => {
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [whitelistAddress, setWhitelistAddress] = useState<WhitelistAddress[]>([]);
  const [isFetchingAddresses, setIsFetchingAddresses] = useState<boolean>(false);
  const [tradeData, setTradeData] = useState<TradeData>({
    fromAsset: "",
    toAsset: "",
    toAddress: "",
    amount: "",
    reference: "",
  });

  const subaccountName = localStorage.getItem("subaccountName");
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const router = useRouter();
  const subaccount_id = localStorage.getItem("subaccountId");

  const getAssetDecimalPlaces = (assetCode: string): number => {
    if (!assetCode) return 2; // Default to 2 decimals

    // First try to find by exact code match (for fiat assets)
    let asset = assets.find((a: any) => a.code === assetCode && !a.crypto);

    // If not found, try to find any asset with this code (for crypto assets)
    if (!asset) {
      asset = assets.find((a: any) => a.code === assetCode);
    }

    return asset?.decimal ?? 2; // Use decimal from asset or default to 2
  };

  const getMinTradeAmount = (assetCode: string): number => {
    if (!assetCode) return 0;

    // First try to find by exact code match (for fiat assets)
    let asset = assets.find((a: any) => a.code === assetCode && !a.crypto);

    // If not found, try to find any asset with this code (for crypto assets)
    if (!asset) {
      asset = assets.find((a: any) => a.code === assetCode);
    }

    return asset?.min_trade ?? 0; // Use min_trade from asset or default to 0
  };

  const validateDecimalPlaces = (value: string, maxDecimals: number): boolean => {
    if (!value || !value.includes('.')) return true;

    const decimalPart = value.split('.')[1];
    return decimalPart ? decimalPart.length <= maxDecimals : true;
  };

  const validateMinTradeAmount = (value: string, minTrade: number): boolean => {
    if (!value || minTrade === 0) return true;

    const numValue = parseFloat(value);
    return !isNaN(numValue) && numValue >= minTrade;
  };

  const fetchWhitelistAddresses = async (toAssetId: string) => {
    if (!toAssetId) {
      setWhitelistAddress([]);
      return;
    }

    // Find the selected crypto asset to get the network
    const selectedCryptoAsset = cryptoAssets.find((asset: any) => asset.id === toAssetId);
    if (!selectedCryptoAsset) {
      setWhitelistAddress([]);
      console.warn("Selected crypto asset not found");
      return;
    }

    const networkName = selectedCryptoAsset.network;

    setIsFetchingAddresses(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/trade/whitelist-addresses`,
        {
          params: {
            network: String(networkName).toUpperCase(),
            status: "1"
          }
        }
      );

      if (response.data?.whitelist_addresses && response.data?.whitelist_addresses?.payload) {
        const mappedAddresses = response.data.whitelist_addresses.payload.map((item: any) => ({
          id: item.wlad_id,
          label: item.nickname,
          address: item.address
        }));
        setWhitelistAddress(mappedAddresses);
      } else {
        setWhitelistAddress([]);
        console.warn("No whitelist addresses found for this asset");
      }
    } catch (error) {
      console.error("Error fetching whitelist addresses:", error);
      setWhitelistAddress([]);

      // Optional: Show error message to user
      if (setSwalProps) {
        setSwalProps({
          show: true,
          icon: "warning",
          title: "Warning",
          html: "Could not load available addresses for this asset.",
          showConfirmButton: true,
          didClose: () => setSwalProps({}),
        });
      }
    } finally {
      setIsFetchingAddresses(false);
    }
  };

  // Effect to fetch addresses when toAsset changes
  useEffect(() => {
    if (tradeData.toAsset) {
      fetchWhitelistAddresses(tradeData.toAsset);
      // Reset toAddress when toAsset changes
      setTradeData(prev => ({
        ...prev,
        toAddress: ""
      }));
    }
  }, [tradeData.toAsset]);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'amount' && tradeData.fromAsset) {
      const maxDecimals = getAssetDecimalPlaces(tradeData.fromAsset);

      // Check if the value has more decimal places than allowed
      if (!validateDecimalPlaces(value, maxDecimals)) {
        return; // Don't update state if validation fails
      }
    }

    setTradeData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenTrade = () => {
    if (
      tradeData.fromAsset &&
      tradeData.toAsset &&
      (isToAssetCrypto() ? tradeData.toAddress : true) &&
      tradeData.amount &&
      tradeData.reference
    ) {
      setIsReviewMode(true);
    }
  };

  const handleConfirmTrade = async () => {
    setIsProcessing(true);
    try {
      const selectedFromAsset = assets.find((asset: any) => asset.code === tradeData.fromAsset);
      const selectedToAsset = cryptoAssets.find((asset: any) => asset.id === tradeData.toAsset);

      let toAsset = tradeData.toAsset;
      let toNetwork = null;
      let fromNetwork = null;
      let wladId = null;
      let addressText = null;
      let toAssetDecimal = 2;

      const fromAssetDecimal = getAssetDecimalPlaces(tradeData.fromAsset);

      if (selectedFromAsset?.crypto) {
        fromNetwork = selectedFromAsset.network;
        const fiatAsset = fiatAssets.find((asset: any) => asset.id === tradeData.toAsset);
        toAsset = fiatAsset?.code || tradeData.toAsset;
        toAssetDecimal = fiatAsset?.decimal ?? 2;
      }
      else if (selectedToAsset?.crypto) {
        toAsset = selectedToAsset.code;
        toNetwork = selectedToAsset.network;
        wladId = tradeData.toAddress;
        addressText = getFullAddressById(tradeData.toAddress);
        toAssetDecimal = selectedToAsset?.decimal ?? 2;
      }
      else {
        const fiatAsset = fiatAssets.find((asset: any) => asset.id === tradeData.toAsset);
        toAsset = fiatAsset?.code || tradeData.toAsset;
        toAssetDecimal = fiatAsset?.decimal ?? 2;
      }

      const postData = {
        from_asset: tradeData.fromAsset,
        from_amount: parseFloat(tradeData.amount),
        from_network: fromNetwork,
        from_asset_decimal: fromAssetDecimal,
        to_asset: toAsset,
        to_network: toNetwork,
        to_asset_decimal: toAssetDecimal,
        wlad_id: wladId,
        to_address: addressText,
        reference: tradeData.reference,
        suba_id: subaccount_id,
        subaccountname: subaccountName,
        username: localStorage.getItem("username"),
      }
      await axios
        .post(process.env.REACT_APP_API_URL + `/api/${userRole}/trade/action/open-trade`, postData)
        .then(async function (response) {
          if (response && response.data?.status === "ok") {
            closeModal();
            if (setSwalProps) {
              setSwalProps({
                show: true,
                icon: 'success',
                title: 'Success',
                html: `Your order has been filled`,
                showConfirmButton: true,
                didClose: async () => {
                  setSwalProps({});
                  if (window.location.pathname !== '/trading/desk') {
                    localStorage.setItem("activeToNewTicketIdFromStorage", response.data?.new_trade_id);
                    router.push(`/${userRole}/trading-desk/desk`);
                  }
                },
              });
            }
          } else {
            setIsProcessing(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: response.data?.message || 'An error occurred while processing your trade',
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsProcessing(false);
              },
            });
          }
        })
        .catch(function (error) {
          let errorMessage = "Trade execution has failed";
          if (
            error.response &&
            (error.response.status === 400 || error.response.status === 500)
          ) {
            errorMessage = error.response.data.message;
          }

          setIsProcessing(false);
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html: errorMessage,
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
              setIsProcessing(false);
            },
          });
        });
    } catch (error) {
      setIsProcessing(false);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
        },
      });
    }
  };

  const closeModal = async () => {
    setTradeData({
      fromAsset: "",
      toAsset: "",
      toAddress: "",
      amount: "",
      reference: "",
    });
    setWhitelistAddress([]);
    setIsReviewMode(false);
    setIsProcessing(false);
    onClose();
  };

  const handleBack = () => {
    setIsReviewMode(false);
  };

  const getAssetName = (assetId: string, isFromAsset: boolean) => {
    // For "From" asset, search in assets (which has both fiat and crypto)
    // For "To" asset, search in cryptoAssets first, then fiatAssets
    let asset;

    if (isFromAsset) {
      asset = assets.find((a: any) => a.id === assetId || a.code === assetId);
    } else {
      // Search in cryptoAssets first (for crypto destinations)
      asset = cryptoAssets.find((a: any) => a.id === assetId || a.code === assetId);
      // If not found, search in fiatAssets (for fiat destinations)
      if (!asset) {
        asset = fiatAssets.find((a: any) => a.id === assetId || a.code === assetId);
      }
    }

    if (!asset) return assetId;

    // For crypto assets, include the network in parentheses
    if (asset.crypto && asset.network) {
      return `${asset.code} (${asset.network})`;
    }

    return asset.name;
  };

  const getFullAddressById = (addressId: string) => {
    const addressObj = whitelistAddress.find(addr => addr.id === addressId);
    return addressObj.address;
  };

  const getAddressLabelById = (addressId: string) => {
    const addressObj = whitelistAddress.find(addr => addr.id === addressId);
    return addressObj?.label ?
      `${addressObj.label} (${formatAddress(addressObj.address)})` :
      formatAddress(addressObj?.address);
  };

  const formatAddress = (address?: string): string => {
    if (!address) return 'N/A';
    return `${address.slice(0, 12)}...${address.slice(-9)}`;
  };

  const handleMaxClick = () => {
    if (tradeData.fromAsset && selectedFromBalance > 0) {
      const maxDecimals = getAssetDecimalPlaces(tradeData.fromAsset);
      const formattedBalance = selectedFromBalance.toFixed(maxDecimals);

      setTradeData(prev => ({
        ...prev,
        amount: formattedBalance
      }));
    }
  };

  const getAvailableBalance = (assetCode: string) => {
    if (!subaccountBalance || !Array.isArray(subaccountBalance)) {
      return 0;
    }
    const balanceItem = subaccountBalance.find((item: any) => item.asset === assetCode);
    return balanceItem ? parseFloat(balanceItem.available) || 0 : 0;
  };

  const getAvailableToAssets = () => {
    if (tradeData.fromAsset === "") {
      return []; // No "To" options until "From" is selected
    }

    // Find the selected "From" asset to check if it's crypto or fiat
    const selectedFromAsset = assets.find((asset: any) =>
      asset.code === tradeData.fromAsset || asset.id === tradeData.fromAsset
    );

    if (!selectedFromAsset) {
      return [];
    }

    // If "From" is Fiat, show Crypto assets with all networks (cryptoAssets)
    if (!selectedFromAsset.crypto) {
      return cryptoAssets;
    }

    // If "From" is Crypto, show only Fiat assets
    return fiatAssets;
  };

  // Add useEffect to reset "To" selection when "From" changes
  useEffect(() => {
    // Reset ALL fields when fromAsset changes (except fromAsset itself)
    setTradeData(prev => ({
      ...prev,
      toAsset: "",
      toAddress: "",
      amount: "",
      reference: ""
    }));
    setWhitelistAddress([]);
  }, [tradeData.fromAsset]);

  const isToAssetCrypto = () => {
    if (!tradeData.toAsset) return false;

    // Check in cryptoAssets first (when From is Fiat)
    let selectedToAsset = cryptoAssets.find((asset: any) => asset.id === tradeData.toAsset);

    // If not found, check in assets (when From is Crypto, To is Fiat)
    if (!selectedToAsset) {
      selectedToAsset = assets.find((asset: any) => asset.id === tradeData.toAsset);
    }

    return selectedToAsset?.crypto === true;
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow navigation and editing keys
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      return;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }

    // Allow only digits and decimal point
    if (!/[0-9.]/.test(e.key)) {
      e.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (e.key === '.' && tradeData.amount.includes('.')) {
      e.preventDefault();
      return;
    }

    // Check decimal places limit when typing after decimal point
    if (tradeData.fromAsset && tradeData.amount.includes('.')) {
      const maxDecimals = getAssetDecimalPlaces(tradeData.fromAsset);
      const currentValue = tradeData.amount;
      const cursorPosition = (e.target as HTMLInputElement).selectionStart || 0;
      const decimalIndex = currentValue.indexOf('.');

      // If typing after decimal point and would exceed max decimals
      if (cursorPosition > decimalIndex && (currentValue.length - decimalIndex - 1) >= maxDecimals) {
        // Only allow if we're replacing/selecting text
        const selectionLength = ((e.target as HTMLInputElement).selectionEnd || 0) - cursorPosition;
        if (selectionLength === 0) {
          e.preventDefault();
        }
      }
    }
  };

  const selectedFromBalance = tradeData.fromAsset ? getAvailableBalance(tradeData.fromAsset) : 0;

  const isFormValid =
    tradeData.fromAsset &&
    tradeData.toAsset &&
    (isToAssetCrypto() ? tradeData.toAddress : true) &&
    tradeData.amount &&
    tradeData.reference &&
    (!tradeData.amount || parseFloat(tradeData.amount) <= selectedFromBalance) &&
    (!tradeData.amount || validateDecimalPlaces(tradeData.amount, getAssetDecimalPlaces(tradeData.fromAsset))) &&
    (!tradeData.amount || validateMinTradeAmount(tradeData.amount, getMinTradeAmount(tradeData.fromAsset)));

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      closeOnOverlayClick={false}
    >
      <ModalOverlay className="bg-[#000] !opacity-30" />
      <ModalContent className="!z-[1002] !m-auto w-full max-w-[500px] px-4 md:top-[12vh]">
        <ModalBody className="p-0 !text-navy-700">
          <Card extra="px-6 pt-8 pb-10 flex flex-col !z-[1004]">
            {!isReviewMode ? (
              <>
                <h1 className="mb-6 text-2xl font-bold text-navy-700 dark:text-white">
                  Open Trade
                </h1>

                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-navy-700 dark:text-white">
                        From <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="fromAsset"
                        value={tradeData.fromAsset}
                        onChange={handleInputChange}
                        disabled={isProcessing || isFetchingTradeAsset || isFetchingAddresses}
                        className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm"
                        required
                      >
                        <option value="">-- Please Select --</option>
                        {assets.filter((item: any) => !item.crypto).length > 0 && assets.filter((item: any) => !item.crypto) &&
                          <optgroup label="Fiat">
                            {assets.filter((item: any) => !item.crypto).map((asset: any) => (
                              <option key={asset.name} value={asset.code}>
                                {asset.name}
                              </option>
                            ))}
                          </optgroup>
                        }
                        {assets.filter((item: any) => item.crypto).length > 0 && assets.filter((item: any) => item.crypto) &&
                          <optgroup label="Crypto">
                            {assets.filter((item: any) => item.crypto).map((asset: any) => (
                              <option key={asset.name} value={asset.code}>
                                {asset.name}
                              </option>
                            ))}
                          </optgroup>
                        }
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-navy-700 dark:text-white">
                        To <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="toAsset"
                        value={tradeData.toAsset}
                        onChange={handleInputChange}
                        disabled={isProcessing || isFetchingTradeAsset || isFetchingAddresses || tradeData.fromAsset === ""}
                        className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm"
                        required
                      >
                        <option value="">-- Please Select --</option>
                        {getAvailableToAssets().map((asset: any) => (
                          <option key={asset.id + "-to"} value={asset.id}>
                            {asset.name}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>

                  {isToAssetCrypto() && (
                    <div>
                      <label className="text-sm font-medium text-navy-700 dark:text-white">
                        To Address <span className="text-red-500">*</span>
                      </label>
                      <Select
                        name="toAddress"
                        value={tradeData.toAddress}
                        onChange={handleInputChange}
                        disabled={isProcessing || isFetchingTradeAsset || isFetchingAddresses || !tradeData.toAsset || whitelistAddress.length === 0}
                        className="h-10 rounded-md custom-select relative bg-white border border-gray-300 focus:outline-none py-2 pl-3 pr-10 sm:text-sm w-full shadow-sm"
                        required
                      >
                        <option value="">
                          {isFetchingAddresses
                            ? "Retrieving..."
                            : !tradeData.toAsset
                              ? "-- Please Select --"
                              : whitelistAddress.length === 0
                                ? "N/A"
                                : "-- Please Select --"
                          }
                        </option>
                        {whitelistAddress.map((address: WhitelistAddress) => (
                          <option key={address.id} value={address.id}>
                            {address.label ? `${address.label} (${formatAddress(address.address)})` : address.address}
                          </option>
                        ))}
                      </Select>
                    </div>
                  )}

                  <div>
                    <InputField
                      disabled={isProcessing || isFetchingTradeAsset || tradeData.fromAsset === ""}
                      variant="auth"
                      extra="shadow-sm"
                      label={`Amount`}
                      required={true}
                      id="amount"
                      name="amount"
                      type="text"
                      placeholder={`Enter amount`}
                      value={tradeData.amount || ""}
                      onKeyDown={handleAmountKeyDown}
                      onChange={handleInputChange}
                      autoComplete="off"
                    />

                    {tradeData.fromAsset && (
                      <div className="mt-2 flex items-center justify-between">
                        <div>
                          {tradeData.amount && parseFloat(tradeData.amount) > selectedFromBalance && (
                            <>
                              <p className="text-xs text-red-500">
                                Insufficient balance.
                              </p>
                              <p className="text-xs text-red-500">
                                Maximum: {selectedFromBalance.toLocaleString(undefined, {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: getAssetDecimalPlaces(tradeData.fromAsset)
                                })} {tradeData.fromAsset}
                              </p>
                            </>
                          )}
                          {tradeData.amount && !validateMinTradeAmount(tradeData.amount, getMinTradeAmount(tradeData.fromAsset)) && (
                            <p className="text-xs text-red-500">
                              Minimum trade amount is {getMinTradeAmount(tradeData.fromAsset)} {tradeData.fromAsset}
                            </p>
                          )}
                          {tradeData.amount && !validateDecimalPlaces(tradeData.amount, getAssetDecimalPlaces(tradeData.fromAsset)) && (
                            <p className="text-xs text-red-500">
                              Maximum {getAssetDecimalPlaces(tradeData.fromAsset)} decimal places allowed for {tradeData.fromAsset}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs text-primary-500 dark:text-primary-400 mr-1">
                            Available Balance: {selectedFromBalance.toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: getAssetDecimalPlaces(tradeData.fromAsset)
                            })} {tradeData.fromAsset}
                          </span>
                          <button
                            type="button"
                            onClick={handleMaxClick}
                            className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-100 cursor-pointer font-medium"
                            disabled={isProcessing || isFetchingTradeAsset}
                          >
                            [max]
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <InputField
                    disabled={isProcessing || isFetchingTradeAsset}
                    variant="auth"
                    extra="shadow-sm"
                    label="Reference"
                    required={true}
                    id="reference"
                    name="reference"
                    type="text"
                    placeholder="Enter reference"
                    value={tradeData.reference}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />

                  <div className="flex gap-3 pt-1">
                    <Button
                      onClick={closeModal}
                      isDisabled={isProcessing || isFetchingTradeAsset}
                      className="btn btn-default w-1/2"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleOpenTrade}
                      isDisabled={!isFormValid || isProcessing || isFetchingTradeAsset || isFetchingAddresses}
                      className="btn btn-primary w-1/2"
                    >
                      Open Trade
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h1 className="mb-6 text-2xl font-bold text-navy-700 dark:text-white">
                  Confirm Open Trade
                </h1>

                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 dark:border-white/10 p-4">
                    <h3 className="mb-3 font-semibold text-navy-700 dark:text-white">
                      Trade Details
                    </h3>

                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">From:</span>
                        <span className="text-sm font-semibold text-navy-700 dark:text-white">
                          {getAssetName(tradeData.fromAsset, true)}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">To:</span>
                        <span className="text-sm font-semibold text-navy-700 dark:text-white">
                          {getAssetName(tradeData.toAsset, false)}
                        </span>
                      </div>

                      {isToAssetCrypto() && (
                        <div className="flex justify-between">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">To Address:</span>
                          <span className="text-sm font-semibold text-navy-700 dark:text-white">
                            {getAddressLabelById(tradeData.toAddress)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Amount:</span>
                        <span className="text-sm font-semibold text-navy-700 dark:text-white">
                          <DisplayAmountWithDecimals amount={Number(tradeData.amount)} currency={tradeData.fromAsset} decimal={getAssetDecimalPlaces(tradeData.fromAsset)} /> {tradeData.fromAsset}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Reference:</span>
                        <span className="text-sm font-semibold text-navy-700 dark:text-white">
                          {tradeData.reference}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Important:</strong> Please review all details carefully before confirming.
                      This action cannot be undone once confirmed.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 pt-6">
                  <Button
                    onClick={handleBack}
                    isDisabled={isProcessing || isFetchingTradeAsset}
                    className="btn btn-default w-1/2"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleConfirmTrade}
                    isDisabled={isProcessing || isFetchingTradeAsset}
                    isLoading={isProcessing || isFetchingTradeAsset}
                    loadingText="Processing..."
                    className="btn btn-primary w-1/2"
                  >
                    Confirm Trade
                  </Button>
                </div>
              </>
            )}
          </Card>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default OpenTradeModal;